import {useState} from "react";
import {Button} from "../../../basic/button";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {Plus} from "lucide-react";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {TeamForm} from "./TeamForm";
import {useCreateTeamMutation} from "../../../../store/generated/api";
import {logger} from "../../../../utils/logger";

interface CreateTeamDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateTeamDialog({open, onOpenChange}: CreateTeamDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [teamName, setTeamName] = useState("");
    const [createTeam, {isLoading}] = useCreateTeamMutation();

    const handleCreateTeam = async () => {
        if (!teamName.trim()) {
            showErrorToast({message: t('admin.teams.createTeam.errors.teamNameRequired')});
            return;
        }

        try {
            logger.info(`Creating team: ${teamName.trim()}`);

            await createTeam({
                vwpCreateTeamRequest: {
                    name: teamName.trim()
                }
            }).unwrap();

            toast.success(t('admin.teams.createTeam.success', {teamName}));
            onOpenChange(false);
            setTeamName("");

            // Cache invalidation is handled automatically by RTK Query
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.teams.createTeam.errors.createFailed')});
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('admin.teams.createTeam.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.teams.createTeam.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <TeamForm
                        teamName={teamName}
                        onTeamNameChange={setTeamName}
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleCreateTeam} disabled={isLoading}>
                        <Plus className="h-4 w-4 mr-2"/>
                        {isLoading ? t('admin.teams.createTeam.creatingButton') : t('admin.teams.createTeam.createButton')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}